import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import usePortal from "../hooks/usePortal";

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  overflow: auto;
  z-index: 999;
`;
const DialogContent = styled.div`
  display: block;
  position: relative;
  width: max-content;
  max-width: calc(100vw - 32px);
  background: linear-gradient(
    135deg,
    rgba(26, 27, 53, 0.95),
    rgba(19, 27, 60, 0.95)
  );
  border-radius: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 999;
  &[data-appearance="default"] {
    position: relative;
    margin: 10vh auto;
  }

  &[data-appearance="centered"] {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

type Props = React.PropsWithChildren<{
  isOpen: boolean;
  onDismiss: () => void;
  appearance?: "default" | "centered";
}>;

const DialogImpl: React.FC<Props> = ({
  isOpen,
  onDismiss,
  appearance = "default",
  ...props
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
      // Click outside
      const handler = (e: MouseEvent) => {
        if (!dialogRef.current?.contains(e.target as HTMLElement)) {
          onDismiss();
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <DialogOverlay>
      <DialogContent
        aria-modal="true"
        role="dialog"
        tabIndex={-1}
        ref={dialogRef}
        data-appearance={appearance}
      >
        {props.children}
      </DialogContent>
    </DialogOverlay>
  );
};

const Dialog: React.FC<Props> = (props) => {
  return usePortal(<DialogImpl {...props} />);
};

export default Dialog;
